import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { UseMutationOptions } from "@tanstack/react-query";

import { addendumKeys } from "@/features/addendum/api/keyFactory";
import { noteKeys } from "@/features/notes/(v2)/api/keyFactory";
import { showToast } from "@/providers/ToastProvider";
import type { UUID } from "@/core/types";

import { cosignatureKeys } from "../keyFactory";
import { rejectDocument, type RejectDocumentVariables } from "../queryFunctions/rejectDocument";

export function useRejectDocument({
  onSuccess,
  ...options
}: UseMutationOptions<void, unknown, RejectDocumentVariables> = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: rejectDocument,
    ...options,
    onError: () => {
      showToast([{ heading: "Something went wrong. Please try again.", variant: "error" }]);
    },
    onSuccess: async (data, variables, context) => {
      /**
       * TODO: Remove when long-polling is available.
       * This is a temporary workaround; see awaitNotesSummaryRefresh for an explanation.
       */
      await new Promise((resolve) => setTimeout(resolve, DELAY_TIME));
      await queryClient.invalidateQueries({ queryKey: cosignatureKeys.base });
      await queryClient.invalidateQueries({
        queryKey: noteKeys.details(variables.documentId as UUID),
      });
      await queryClient.invalidateQueries({
        queryKey: noteKeys.summary(variables.patientId as UUID),
      });
      await queryClient.invalidateQueries({
        queryKey: addendumKeys.byNote(variables.documentId as UUID),
      });

      onSuccess?.(data, variables, context);

      const heading = `${variables.documentType === "note_addendum" ? "Addendum" : "Note"} rejected with your feedback.`;

      showToast([{ heading, variant: "success" }, { delay: 500 }]);
    },
  });
}

const DELAY_TIME = 4_000;
